<template>
  <div class="skills-section section">
    <div class="section-header-row">
      <h2 class="section-title">
        <span class="icon"><i class="mdi mdi-lightbulb-on"></i></span>
        Top Skills
      </h2>
      <b-button
        v-if="editable"
        type="is-primary"
        class="update-btn"
        @click="openEditModal"
      >
        <i class="mdi mdi-pencil"></i>
        Edit
      </b-button>
    </div>
    <div class="skill-tags">
      <span
        v-for="(skill, index) in localSkills"
        :key="`skill-${index}`"
        class="skill-tag"
      >
        {{ skill }}
      </span>
    </div>

    <b-modal
      v-model="showEditModal"
      :can-cancel="['escape', 'outside', 'button']"
      :width="520"
      trap-focus
    >
      <div class="modal-content">
        <h2 class="modal-title">
          <i class="mdi mdi-pencil"></i>
          Edit Top Skills
        </h2>
        <p class="modal-desc">
          Reorder, remove, or add skills. You can have up to 5 total.
        </p>

        <div class="skills-edit-list">
          <div
            v-for="(skill, index) in editableSkills"
            :key="`editable-skill-${index}`"
            class="edit-skill-row"
          >
            <span class="edit-skill-text">{{ skill }}</span>
            <div class="edit-skill-actions">
              <b-button
                type="is-text"
                size="is-small"
                :disabled="index === 0"
                @click="moveSkill(index, -1)"
              >
                <i class="mdi mdi-chevron-up"></i>
              </b-button>
              <b-button
                type="is-text"
                size="is-small"
                :disabled="index === editableSkills.length - 1"
                @click="moveSkill(index, 1)"
              >
                <i class="mdi mdi-chevron-down"></i>
              </b-button>
              <b-button
                type="is-text"
                size="is-small"
                class="remove-skill-btn"
                @click="removeSkill(index)"
              >
                <i class="mdi mdi-close"></i>
              </b-button>
            </div>
          </div>
        </div>

        <b-field label="Add a skill" class="add-skill-field" grouped>
          <b-input
            v-model="newSkill"
            placeholder="Type a new skill..."
            :disabled="editableSkills.length >= maxSkills"
            @keyup.enter="addSkill"
          />
          <p class="control">
            <b-button
              type="is-primary"
              :disabled="editableSkills.length >= maxSkills"
              @click="addSkill"
            >
              Add
            </b-button>
          </p>
        </b-field>
        <p v-if="editableSkills.length >= maxSkills" class="max-skill-note">
          You already have 5 skills. Remove one to add a new skill.
        </p>

        <div class="modal-actions">
          <b-button
            type="is-primary"
            :loading="saving"
            @click="saveSkills"
          >
            Save
          </b-button>
          <b-button type="is-text" @click="showEditModal = false">
            Cancel
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { useUserStore } from '@/stores/userStore'

export default {
  name: 'SkillsSection',
  props: {
    skills: {
      type: Array,
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showEditModal: false,
      editableSkills: [],
      localSkills: [],
      newSkill: '',
      saving: false,
      maxSkills: 5
    }
  },
  created() {
    this.userStore = useUserStore()
    this.localSkills = Array.isArray(this.skills) ? [...this.skills] : []
  },
  watch: {
    skills: {
      handler(newSkills) {
        this.localSkills = Array.isArray(newSkills) ? [...newSkills] : []
        if (!this.showEditModal) {
          this.editableSkills = [...this.localSkills]
        }
      },
      immediate: true
    }
  },
  methods: {
    openEditModal() {
      this.editableSkills = [...this.localSkills]
      this.newSkill = ''
      this.showEditModal = true
    },
    moveSkill(index, direction) {
      const nextIndex = index + direction
      if (nextIndex < 0 || nextIndex >= this.editableSkills.length) return
      const updated = [...this.editableSkills]
      const [moved] = updated.splice(index, 1)
      updated.splice(nextIndex, 0, moved)
      this.editableSkills = updated
    },
    removeSkill(index) {
      this.editableSkills.splice(index, 1)
    },
    addSkill() {
      const trimmed = this.newSkill.trim()
      if (!trimmed) return
      if (this.editableSkills.length >= this.maxSkills) {
        this.$buefy.toast.open({
          message: 'You already have 5 skills. Remove one to add a new skill.',
          type: 'is-warning'
        })
        return
      }
      if (this.editableSkills.some(skill => skill.toLowerCase() === trimmed.toLowerCase())) {
        this.$buefy.toast.open({
          message: 'That skill is already listed.',
          type: 'is-warning'
        })
        return
      }
      this.editableSkills.push(trimmed)
      this.newSkill = ''
    },
    async saveSkills() {
      if (this.editableSkills.length > this.maxSkills) return
      this.saving = true
      try {
        const requestConfig = this.config || {
          headers: { Authorization: this.$cookies.get('teamwrkr-token') }
        }
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/update-skills`,
          { skills: this.editableSkills },
          requestConfig
        )
        if (response?.data) {
          this.userStore.setUser(response.data)
        }
        this.localSkills = [...this.editableSkills]
        this.$emit('skills-updated', this.localSkills)
        this.$buefy.toast.open({
          message: 'Skills updated successfully.',
          type: 'is-success'
        })
        this.showEditModal = false
      } catch (err) {
        this.$buefy.toast.open({
          message: 'Failed to update skills.',
          type: 'is-danger'
        })
      } finally {
        this.saving = false
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  color: $color-text;
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid $color-border-muted;
  gap: 0.5rem;
  width: 100%;

  .icon {
    margin-right: 12px;
    color: $color-primary;
  }
}

.section-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1.5rem;
}

.update-btn {
  margin-bottom: 0;
  font-weight: 700;
  font-size: 1.08rem;
  height: 3rem;
  border-radius: 0.7rem;
  background-color: $color-primary;
  color: $color-surface;
  box-shadow: 0 2px 8px rgba(170, 118, 214, 0.07);
  margin-top: -1rem;
  &:hover, &:focus {
    background: $color-primary;
    color: $color-surface;
  }
}

.skill-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  .skill-tag {
    background-color: $color-primary;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.modal-content {
  padding: 1.5rem;
  background: $color-surface;
  border-radius: 0.75rem;
  color: $color-text;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: $color-primary;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-desc {
  font-size: 1rem;
  color: $color-text-muted;
}

.skills-edit-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.edit-skill-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: $color-surface-soft;
  border-radius: 10px;
  padding: 0.6rem 0.8rem;
}

.edit-skill-text {
  font-weight: 600;
}

.edit-skill-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.remove-skill-btn {
  color: $color-danger;
}

.max-skill-note {
  color: $color-warning;
  font-size: 0.9rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}
</style>
